"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"

type Language = "en" | "fr" | "de" | "es"

type LanguageContextType = {
  language: Language
  setLanguage: (language: Language) => void
  t: (key: string) => string
}

const translations = {
  en: {
    "nav.home": "Home",
    "nav.about": "About",
    "nav.services": "Services",
    "nav.portfolio": "Portfolio",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "hero.title": "Hamid Ayub",
    "hero.subtitle": "Software Engineer",
    "hero.description": "Specializing in Blockchain, GenAI, and Enterprise Solutions",
    "hero.cta": "Get in Touch",
    "about.title": "About Me",
    "services.title": "Services",
    "portfolio.title": "Portfolio",
    "testimonials.title": "Testimonials",
    "blog.title": "Blog",
    "contact.title": "Contact Me",
    // Add more translations as needed
  },
  fr: {
    "nav.home": "Accueil",
    "nav.about": "À propos",
    "nav.services": "Services",
    "nav.portfolio": "Portfolio",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "hero.title": "Hamid Ayub",
    "hero.subtitle": "Ingénieur Logiciel",
    "hero.description": "Spécialisé dans la Blockchain, GenAI et Solutions d'Entreprise",
    "hero.cta": "Contactez-moi",
    "about.title": "À propos de moi",
    "services.title": "Services",
    "portfolio.title": "Portfolio",
    "testimonials.title": "Témoignages",
    "blog.title": "Blog",
    "contact.title": "Contactez-moi",
    // Add more translations as needed
  },
  de: {
    "nav.home": "Startseite",
    "nav.about": "Über mich",
    "nav.services": "Dienstleistungen",
    "nav.portfolio": "Portfolio",
    "nav.blog": "Blog",
    "nav.contact": "Kontakt",
    "hero.title": "Hamid Ayub",
    "hero.subtitle": "Software-Ingenieur",
    "hero.description": "Spezialisiert auf Blockchain, GenAI und Unternehmenslösungen",
    "hero.cta": "Kontaktieren Sie mich",
    "about.title": "Über mich",
    "services.title": "Dienstleistungen",
    "portfolio.title": "Portfolio",
    "testimonials.title": "Referenzen",
    "blog.title": "Blog",
    "contact.title": "Kontaktieren Sie mich",
    // Add more translations as needed
  },
  es: {
    "nav.home": "Inicio",
    "nav.about": "Acerca de",
    "nav.services": "Servicios",
    "nav.portfolio": "Portafolio",
    "nav.blog": "Blog",
    "nav.contact": "Contacto",
    "hero.title": "Hamid Ayub",
    "hero.subtitle": "Ingeniero de Software",
    "hero.description": "Especializado en Blockchain, GenAI y Soluciones Empresariales",
    "hero.cta": "Contáctame",
    "about.title": "Sobre mí",
    "services.title": "Servicios",
    "portfolio.title": "Portafolio",
    "testimonials.title": "Testimonios",
    "blog.title": "Blog",
    "contact.title": "Contáctame",
    // Add more translations as needed
  },
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("en")

  useEffect(() => {
    // Check if window is defined (client-side)
    if (typeof window !== "undefined") {
      const savedLanguage = localStorage.getItem("language") as Language
      if (savedLanguage && ["en", "fr", "de", "es"].includes(savedLanguage)) {
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => {
          setLanguage(savedLanguage)
        }, 0)
      }
    }
  }, [])

  useEffect(() => {
    // Check if window is defined (client-side)
    if (typeof window !== "undefined") {
      localStorage.setItem("language", language)
    }
  }, [language])

  const t = (key: string): string => {
    return translations[language][key as keyof (typeof translations)[typeof language]] || key
  }

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

